import { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, EmptyState, Paper, Spinner, TextField, Typography } from '@glplus/glpweb-dsl';
import { useCapabilities } from '../lib/useCapabilities';
import { NEUTRAL_BTN } from '../lib/buttonStyles';
import {
  adminApiClient,
  type RunnerPhotoKind,
  type RunnerPhotoQueueItem,
  type RunnerPhotoQueueResult,
} from '../lib/adminApiClient';

/**
 * §18c Runner photo review — the anti-account-sharing control.
 *
 * The identity layers in §5a end at Checkr. None of that holds if the face a
 * household opens the door to was uploaded freely and never tied to the person
 * who cleared, so every first upload AND every change lands here.
 *
 * 🔎 Unlike ProductImageQueue, this screen DOES render the image — and must.
 * That queue withholds previews because its URLs are third-party and rendering
 * them would breach "we serve only our own hosted images" and leak admin IPs.
 * These are our own objects in our own private bucket, reached by a presigned
 * URL minted for this response, and §18c requires a human to actually look at
 * the face. Do not "fix" this by removing the preview.
 *
 * 🛑 No automated face-matching, at launch or by drift (§18c): that is biometric
 * processing with its own consent/retention posture (BIPA / CUBI / MHMDA).
 */

const CHECKS = [
  'It is a real person, and it is *this* person (compare against the ID from onboarding)',
  'Face visible and unobstructed — no sunglasses, no heavy shadow',
  'No other people or pets, and not a cropped group photo',
  'Nothing inappropriate',
  'No text, logos, handles or phone numbers — those route around in-app-only messaging',
];

const TABS: { key: RunnerPhotoKind | 'all'; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'profile', label: 'Face' },
  { key: 'vehicle', label: 'Vehicle' },
];

export default function RunnerPhotoQueue() {
  const caps = useCapabilities();
  const canApprove = caps.has('runner-photo:review');
  const canReject = caps.has('runner-photo:reject');

  const [kind, setKind] = useState<RunnerPhotoKind | 'all'>('all');
  const [data, setData] = useState<RunnerPhotoQueueResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<{ kind: 'success' | 'error'; msg: string } | null>(null);

  const load = useCallback(async (first: boolean) => {
    if (first) setLoading(true);
    setError(null);
    try {
      setData(await adminApiClient.listRunnerPhotoQueue({ kind }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load the queue');
    } finally {
      if (first) setLoading(false);
    }
  }, [kind]);

  useEffect(() => {
    void load(true);
  }, [load]);

  async function decide(item: RunnerPhotoQueueItem, k: RunnerPhotoKind, decision: 'approve' | 'reject', reason?: string) {
    setBusyId(item.id);
    setFlash(null);
    try {
      await adminApiClient.reviewRunnerPhoto(item.id, { kind: k, decision, reason });
      await load(false);
      setFlash({
        kind: 'success',
        msg: decision === 'approve'
          ? `${item.firstName} ${item.lastName} — ${k === 'profile' ? 'photo' : 'vehicle photo'} approved and live.`
          : `${item.firstName} ${item.lastName} — sent back with your note.`,
      });
    } catch (err) {
      setFlash({ kind: 'error', msg: err instanceof Error ? err.message : 'That did not go through.' });
    } finally {
      setBusyId(null);
    }
  }

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', pt: 10 }}><Spinner aria-label="Loading the photo queue" /></Box>;
  }

  const items = data?.items ?? [];

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>Runner photos</Typography>
      <Typography sx={{ color: '#6b7280', fontSize: '14px', mb: 2 }}>
        The face a household opens the door to. Every first upload and every change is reviewed here, against
        the ID verified at onboarding.
      </Typography>

      {error && <Box sx={{ mb: 2 }}><Alert severity="error">{error}</Alert></Box>}
      {flash && <Box sx={{ mb: 2 }}><Alert severity={flash.kind}>{flash.msg}</Alert></Box>}

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
        {TABS.map((t) => (
          <Button
            key={t.key}
            ariaLabel={`Show ${t.label}`}
            buttonText={
              t.key === 'profile' ? `Face (${data?.counts.profile ?? 0})`
                : t.key === 'vehicle' ? `Vehicle (${data?.counts.vehicle ?? 0})`
                  : `All (${(data?.counts.profile ?? 0) + (data?.counts.vehicle ?? 0)})`
            }
            size="small"
            variant={kind === t.key ? 'contained' : 'outlined'}
            sx={kind === t.key ? undefined : NEUTRAL_BTN}
            onClick={() => setKind(t.key)}
          />
        ))}
      </Box>

      {!items.length ? (
        <EmptyState
          title="Nothing waiting"
          body="No Runner photos are in review. New uploads land here as soon as they are submitted."
        />
      ) : (
        <Box sx={{ display: 'grid', gap: 2 }}>
          {items.map((item) => (
            <ReviewRow
              key={item.id}
              item={item}
              busy={busyId === item.id}
              canApprove={canApprove}
              canReject={canReject}
              onDecide={decide}
            />
          ))}
        </Box>
      )}

      <Paper sx={{ p: 2, mt: 3, backgroundColor: '#f8fafc' }}>
        <Typography sx={{ fontWeight: 700, fontSize: '14px', mb: 1 }}>What you are checking</Typography>
        {CHECKS.map((c) => (
          <Typography key={c} sx={{ color: '#4b5563', fontSize: '13px', py: 0.3 }}>• {c}</Typography>
        ))}
        <Typography sx={{ color: '#9aa3ab', fontSize: '12px', mt: 1.5 }}>
          Approving is what puts this face in front of households. A rejection needs a reason — the Runner
          reads it word for word, so write it as you would say it to them.
        </Typography>
      </Paper>
    </Box>
  );
}

function ReviewRow({ item, busy, canApprove, canReject, onDecide }: {
  item: RunnerPhotoQueueItem;
  busy: boolean;
  canApprove: boolean;
  canReject: boolean;
  onDecide: (item: RunnerPhotoQueueItem, kind: RunnerPhotoKind, decision: 'approve' | 'reject', reason?: string) => void;
}) {
  const [reason, setReason] = useState('');
  const [rejecting, setRejecting] = useState<RunnerPhotoKind | null>(null);

  const rows: { kind: RunnerPhotoKind; submitted: string | null; live: string | null; label: string }[] = [];
  if (item.photoStatus === 'pending') {
    rows.push({ kind: 'profile', submitted: item.submittedPhotoViewUrl, live: item.livePhotoViewUrl, label: 'Face' });
  }
  if (item.vehiclePhotoStatus === 'pending') {
    rows.push({ kind: 'vehicle', submitted: item.submittedVehicleViewUrl, live: null, label: 'Vehicle' });
  }

  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 1.5 }}>
        <Typography sx={{ fontWeight: 800, fontSize: '15px' }}>{item.firstName} {item.lastName}</Typography>
        <Chip label={item.metro} size="small" />
        <Chip label={item.lifecycle} size="small" />
        {item.glpdId && <Chip label={item.glpdId} size="small" />}
        <Typography sx={{ color: '#9aa3ab', fontSize: '12px', ml: 'auto' }}>
          Waiting since {new Date(item.updatedAt).toLocaleString()}
        </Typography>
      </Box>

      {rows.map((r) => (
        <Box key={r.kind} sx={{ borderTop: '1px solid #eef2f6', pt: 2, mt: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '13px', mb: 1 }}>{r.label}</Typography>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <Preview url={r.submitted} label="Submitted" ring="#F67A1A" />
            {/* The photo it would replace — §18c is a comparison, not a spot check. */}
            {r.kind === 'profile' && <Preview url={r.live} label="Currently live" />}
            <Box sx={{ flex: 1, minWidth: 280 }}>
              {rejecting === r.kind ? (
                <>
                  <TextField
                    ariaLabel="Reason the Runner will read"
                    label="Reason the Runner will read"
                    size="small"
                    fullWidth
                    value={reason}
                    disabled={busy}
                    onChange={(e) => setReason(e.target.value.slice(0, 120))}
                    placeholder="Face is partly out of frame — please retake in better light."
                  />
                  <Typography sx={{ color: '#9aa3ab', fontSize: '12px', mt: 0.5 }}>
                    {reason.length}/120 · sent to them word for word.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
                    <Button
                      ariaLabel="Send it back"
                      buttonText={busy ? 'Sending…' : 'Send it back'}
                      size="small"
                      variant="contained"
                      disabled={busy || !reason.trim()}
                      sx={{ backgroundColor: '#b42318', '&:hover': { backgroundColor: '#912018' } }}
                      onClick={() => onDecide(item, r.kind, 'reject', reason.trim())}
                    />
                    <Button
                      ariaLabel="Cancel"
                      buttonText="Cancel"
                      size="small"
                      variant="outlined"
                      sx={NEUTRAL_BTN}
                      disabled={busy}
                      onClick={() => { setRejecting(null); setReason(''); }}
                    />
                  </Box>
                </>
              ) : (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    ariaLabel={`Approve the ${r.label.toLowerCase()} photo`}
                    buttonText={busy ? 'Working…' : 'Approve'}
                    size="small"
                    variant="contained"
                    disabled={busy || !canApprove}
                    onClick={() => onDecide(item, r.kind, 'approve')}
                  />
                  <Button
                    ariaLabel={`Send the ${r.label.toLowerCase()} photo back`}
                    buttonText="Send it back"
                    size="small"
                    variant="outlined"
                    sx={NEUTRAL_BTN}
                    disabled={busy || !canReject}
                    onClick={() => setRejecting(r.kind)}
                  />
                  {!canApprove && !canReject && (
                    <Typography sx={{ color: '#9aa3ab', fontSize: '12px', alignSelf: 'center' }}>
                      You have view-only access to Runner photos.
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      ))}
    </Paper>
  );
}

function Preview({ url, label, ring }: { url: string | null; label: string; ring?: string }) {
  return (
    <Box>
      <Box
        role="img"
        aria-label={url ? `${label} photo` : `No ${label.toLowerCase()} photo`}
        sx={{
          width: 160, height: 160, borderRadius: 2, backgroundColor: '#e5e7eb',
          backgroundImage: url ? `url(${url})` : 'none', backgroundSize: 'cover', backgroundPosition: 'center',
          border: ring ? `3px solid ${ring}` : '3px solid #e5e7eb',
        }}
      />
      <Typography sx={{ fontSize: '12px', fontWeight: 700, mt: 0.75 }}>{label}</Typography>
      {!url && <Typography sx={{ fontSize: '12px', color: '#9aa3ab' }}>none</Typography>}
    </Box>
  );
}
